import React, { useEffect, useState } from 'react';
import {
    Modal, Box, Typography, TextField, Button, MenuItem, Select, InputLabel, FormControl, CircularProgress,
    Alert
} from '@mui/material';
import useAxiosPrivate from '../hooks/useAxiosPrivate';


export default function CreateBatchMagicProductModal({ open, onClose, data, setResponseData }) {
    const axiosPrivate = useAxiosPrivate();
    const [form, setForm] = useState({
        name: '',
        external_ref: '',
        supplier_id: '',
        group: '',
        weight_in_a_colli: '',
        price_per_kilo: '',
        org_id: 1,
        image: ''
    });
    const [suppliers, setSuppliers] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (data) {
            setForm(prev => ({
                ...prev,
                external_ref: data.item_number || '',
                name: data.product_name || '',
                price_per_kilo: data.kilo_price || '',

            }));
        }
    }, [data]);

    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                const res = await axiosPrivate.get('batch-magic/suppliers/all');
                setSuppliers(res.data?.data || []);
            } catch (error) {
                console.error('Failed to load suppliers', error);
            }
        };
        loadSuppliers();
    }, [axiosPrivate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm(prev => ({ ...prev, image: reader.result }));
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const res = await axiosPrivate.post('batch-magic/create/product', form);
            if (res.data?.success) {
                alert('Product created successfully');
                if (setResponseData && typeof setResponseData === 'function') {
                    const createdData = res.data?.data;
                    setResponseData(prev => {
                        const updatedLineItems = prev.line_items.map(item =>
                            item.item_number === createdData.external_ref
                                ? { ...item, bm_sync: true, bm_kilo_price: createdData.price_per_kilo }
                                : item
                        );
                        return { ...prev, line_items: updatedLineItems };
                    });
                }
                onClose();
            } else {
                setErrorMessage(res.data?.message || 'Failed to create product');
            }
        } catch (error) {
            console.error('Create product error', error);
            setErrorMessage('Error creating product');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 500,
                bgcolor: 'background.paper',
                border: '2px solid #000',
                boxShadow: 24,
                p: 4,
            }}>
                <Typography variant="h6" gutterBottom>
                    Create Batch Magic Product
                </Typography>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
                )}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Product Name"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="External Ref"
                    name="external_ref"
                    value={form.external_ref}
                    onChange={handleInputChange}
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Supplier</InputLabel>
                    <Select
                        name="supplier_id"
                        value={form.supplier_id}
                        onChange={handleInputChange}
                        label="Supplier"
                    >
                        {suppliers.map(supplier => (
                            <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel>Group</InputLabel>
                    <Select
                        name="group"
                        value={form.group}
                        onChange={handleInputChange}
                        label="Group"
                    >
                        <MenuItem value="With sugar">With sugar</MenuItem>
                        <MenuItem value="Without sugar">Without sugar</MenuItem>
                        <MenuItem value="Wrapped">Wrapped</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    margin="normal"
                    label="Weight in a Colli"
                    name="weight_in_a_colli"
                    type="number"
                    value={form.weight_in_a_colli}
                    onChange={handleInputChange}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Price per Kilo (without sugar tax)"
                    name="price_per_kilo"
                    type="number"
                    value={form.price_per_kilo}
                    onChange={handleInputChange}
                />

                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                    Upload Image
                    <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </Button>

                {previewImage && (
                    <Box mt={2}>
                        <Typography variant="subtitle2">Image Preview:</Typography>
                        <img src={previewImage} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                    </Box>
                )}

                <Box mt={3} display="flex" justifyContent="flex-end">
                    <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Create Product'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
